// src/pages/reports/LoginOtp/ReportsLogTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  IconButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Select,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PaginationReports from "../../brands/PaginationReports";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import Export from "../../../../src/assests/images/export.svg";
import * as XLSX from "xlsx";
import { getToken } from "../../../utils/utils";
import { exportTaskTransExportCSV, exportTaskTransReport, TaskTransReportRequest } from "../api";

const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
    }}
  />
);

type Order = "asc" | "desc";

interface ReportsTaskTrans {
  searchQuery: string;
  dateRange: [Date | null, Date | null];
  appliedFilters?: {
    locations?: string[];
    outlet_category?: string[];
    pmi_entity_code?: string[];
    outlet_name?: string[];
    task_id?: (string | number)[];
    transaction_from?: string | null;
    transaction_to?: string | null;
    search_key?: string | null;
  } | null;
}

const RAW_COLUMNS = [
  { label: "User First Name", value: "User First Name" },
  { label: "User Last Name", value: "User Last Name" },
  { label: "Outlet Name", value: "Outlet Name" },
  { label: "City", value: "City" },
  { label: "PMI Entity Code", value: "PMI Entity Code" },
  { label: "User ID", value: "User ID" },
  { label: "Transaction Date", value: "Transaction Date" },
  { label: "Region", value: "Region" },
  { label: "ASM Name", value: "ASM Name" },
  { label: "Area Code", value: "Area Code" },
  { label: "TSE Name", value: "TSE Name" },
  { label: "Territory Code", value: "Territory Code" },
  { label: "Outlet Category", value: "Outlet Category" },
  { label: "Phone Number", value: "Phone Number" },
  { label: "Outlet Latitude", value: "Outlet Latitude" },
  { label: "Outlet Longitude", value: "Outlet Longitude" },
  { label: "Task Performance Latitude", value: "Task Performance Latitude" },
  { label: "Task Performance Longitude", value: "Task Performance Longitude" },
  { label: "Distance Of Task", value: "Distance Of Task" },
  { label: "Task Start Date", value: "Task Start Date" },
  { label: "Task End Date", value: "Task End Date" },
  { label: "Task Category", value: "Task Category" },
  { label: "Task Type", value: "Task Type" },
  { label: "Task ID", value: "Task ID" },
  { label: "Task Name", value: "Task Name" },
  { label: "Variant", value: "Variant" },
  { label: "Sub Variant", value: "Sub Variant" },
  { label: "Completion", value: "Completion" },
  { label: "Duration", value: "Duration" },
  { label: "Task Completion In (hrs)", value: "Task Completion In (hrs)" },
  { label: "Total Points", value: "Total Points" },
  { label: "No. Of Sub-Tasks", value: "No. Of Sub-Tasks" },
  { label: "Total Sub-Task Points", value: "Total Sub-Task Points" },
  { label: "Sub-Task ID", value: "Sub-Task ID" },
  { label: "Bonus Points Earned", value: "Bonus Points Earned" },
  { label: "Points Earned", value: "Points Earned" },
  { label: "Task Status", value: "Task Status" },
  { label: "Task Completed Date", value: "Task Completed Date" },
  { label: "Task Completed Time", value: "Task Completed Time" },
  { label: "Approval Status", value: "Approval Status" },
  { label: "Approval Or Rejection Date", value: "Approval Or Rejection Date" },
  { label: "Scanned Code", value: "Scanned Code" },
  { label: "Image Link", value: "Image Link" },
] as const;

type ColumnLabel = (typeof RAW_COLUMNS)[number]["label"];
type ColumnKey = (typeof RAW_COLUMNS)[number]["value"];

const ALL_LABELS: ColumnLabel[] = RAW_COLUMNS.map((c) => c.label);
const labelToKey = RAW_COLUMNS.reduce<Record<ColumnLabel, ColumnKey>>((acc, c) => {
  acc[c.label] = c.value;
  return acc;
}, {} as any);

const PARENT_COLUMNS: ColumnLabel[] = [
  "User ID",
  "User First Name",
  "User Last Name",
  "PMI Entity Code",
  "Outlet Name",
  "City",
];
const DETAIL_COLUMNS: ColumnLabel[] = ALL_LABELS.filter((c) => !PARENT_COLUMNS.includes(c));

const DEFAULT_VISIBLE_PARENTS: ColumnLabel[] = [...PARENT_COLUMNS];
const DEFAULT_PARENTS_SET = new Set<ColumnLabel>(DEFAULT_VISIBLE_PARENTS);
const withDefaultsIfEmpty = (arr: ColumnLabel[]) =>
  arr && arr.length ? arr : [...DEFAULT_VISIBLE_PARENTS];

type ParentOrderCol = (typeof PARENT_COLUMNS)[number];

const TaskTrans: React.FC<ReportsTaskTrans> = ({
  searchQuery,
  dateRange: parentDateRange,
  appliedFilters = null,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const [visibleParentColumns, setVisibleParentColumns] = useState<ColumnLabel[]>(
    DEFAULT_VISIBLE_PARENTS
  );
  const [tempSelectedColumns, setTempSelectedColumns] =
    useState<ColumnLabel[]>(DEFAULT_VISIBLE_PARENTS);
  const [colSelectOpen, setColSelectOpen] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  const filteredColumns = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) return PARENT_COLUMNS;
    return PARENT_COLUMNS.filter((name) => name.toLowerCase().includes(q));
  }, [colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 && filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      setTempSelectedColumns((prev) => {
        const next = prev.filter((p) => !filteredColumns.includes(p) || DEFAULT_PARENTS_SET.has(p));
        DEFAULT_VISIBLE_PARENTS.forEach((d) => {
          if (!next.includes(d)) next.push(d);
        });
        return next;
      });
    } else {
      setTempSelectedColumns((prev) =>
        Array.from(
          new Set<ColumnLabel>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE_PARENTS])
        )
      );
    }
  };

  const [orderBy, setOrderBy] = useState<ParentOrderCol>("User ID");
  const [order, setOrder] = useState<Order>("asc");

  const localDateRange = parentDateRange;

  const toYMD = (d?: Date | null) => {
    if (!d) return undefined;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const FETCH_LIMIT = 100000;
  const buildPayload = () => {
    const [start, end] = localDateRange;
    const txnFrom = appliedFilters?.transaction_from ?? toYMD(start);
    const txnTo = appliedFilters?.transaction_to ?? toYMD(end);
    const searchKey = (appliedFilters?.search_key ?? searchQuery)?.trim();

    return {
      page: 1,
      limit: FETCH_LIMIT,
      ...(searchKey ? { search_key: searchKey } : {}),
      ...(appliedFilters?.search_key ? { search_key: appliedFilters.search_key } : {}),
      ...(txnFrom && txnTo ? { transaction_from: txnFrom, transaction_to: txnTo } : {}),
      ...(appliedFilters?.locations?.length ? { locations: appliedFilters.locations } : {}),
      ...(appliedFilters?.outlet_category?.length
        ? { outlet_category: appliedFilters.outlet_category }
        : {}),
      ...(appliedFilters?.pmi_entity_code?.length
        ? { pmi_entity_code: appliedFilters.pmi_entity_code } : {}),
      ...(appliedFilters?.outlet_name?.length ? { outlet_name: appliedFilters.outlet_name } : {}),
      ...(appliedFilters?.task_id?.length ? { task_id: appliedFilters.task_id } : {}),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const payload = buildPayload();
        const response = await exportTaskTransReport(payload, token!);
        const resultsArray = response?.results ?? [];
        setRows(resultsArray);
        setPage(1);
      } catch (err) {
        console.error("Failed to fetch transaction report:", err);
        setRows([]);
      }
    };

    fetchData();
  }, [
    searchQuery,
    JSON.stringify(appliedFilters),
    localDateRange?.[0]?.getTime?.(),
    localDateRange?.[1]?.getTime?.(),
  ]);

  type Parent = { parent: any; children: any[] };
  const grouped = useMemo<Parent[]>(() => {
    const byUser = new Map<string | number, Parent>();
    for (const r of rows) {
      const userId = r?.["User ID"];
      if (userId === null || userId === undefined) continue;
      if (!byUser.has(userId)) {
        byUser.set(userId, { parent: r, children: [] });
      }
      byUser.get(userId)!.children.push(r);
    }
    return Array.from(byUser.values());
  }, [rows]);

  const sortedParents = useMemo(() => {
    const copy = [...grouped];
    copy.sort((a, b) => {
      const av = a.parent?.[orderBy];
      const bv = b.parent?.[orderBy];

      if (orderBy === "User ID") {
        const an = Number(av);
        const bn = Number(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) {
          return order === "asc" ? an - bn : bn - an;
        }
      }

      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as < bs) return order === "asc" ? -1 : 1;
      if (as > bs) return order === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [grouped, orderBy, order]);

  const totalParents = sortedParents.length;
  const totalPages = Math.max(1, Math.ceil(totalParents / itemsPerPage));
  const pagedParents = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedParents.slice(start, start + itemsPerPage);
  }, [sortedParents, page, itemsPerPage]);

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>({});
  const toggleExpand = (userId: string | number) => {
    setExpanded((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleSort = (label: ParentOrderCol) => {
    const isAsc = orderBy === label && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(label);
  };

  const handleExport = async () => {
    try {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");

      const userParentLabels: ColumnLabel[] = visibleParentColumns;
      const detailLabels: ColumnLabel[] = DETAIL_COLUMNS;

      const selectedLabels: ColumnLabel[] = Array.from(
        new Set<ColumnLabel>([...userParentLabels, ...detailLabels])
      );
      const selectedKeys: ColumnKey[] = selectedLabels.map((l) => labelToKey[l]);

      const [start, end] = localDateRange;
      const toYMD = (d?: Date | null) => {
        if (!d) return undefined;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      const txnFrom = appliedFilters?.transaction_from ?? toYMD(start);
      const txnTo = appliedFilters?.transaction_to ?? toYMD(end);
      const searchKey = (appliedFilters?.search_key ?? searchQuery)?.trim();

      const filters: TaskTransReportRequest = {
        ...(searchKey ? { search_key: searchKey } : {}),
        ...(appliedFilters?.search_key ? { search_key: appliedFilters.search_key } : {}),
        ...(txnFrom && txnTo ? { transaction_from: txnFrom, transaction_to: txnTo } : {}),
        ...(appliedFilters?.locations?.length ? { locations: appliedFilters.locations } : {}),
        ...(appliedFilters?.outlet_category?.length ? { outlet_category: appliedFilters.outlet_category } : {}),
        ...(appliedFilters?.pmi_entity_code?.length ? { pmi_entity_code: appliedFilters.pmi_entity_code } : {}),
        ...(appliedFilters?.outlet_name?.length ? { outlet_name: appliedFilters.outlet_name } : {}),
        ...(appliedFilters?.task_id?.length ? { task_id: appliedFilters.task_id } : {}),
      };

      const { blob, filename } = await exportTaskTransExportCSV(
        {
          filters,
          scope: "filtered",
          columnsMode: "selected",
          selectedColumns: selectedKeys,
          stream: true,
        },
        token!
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `task_transaction_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const renderDisplay = (v: any) =>
    v === null || v === undefined || v === "" ? "-" : String(v);

  const formatDMonY = (raw: any) => {
    if (!raw) return "-";
    const d = new Date(String(raw));
    if (isNaN(d.getTime())) return String(raw);
    const day = String(d.getDate()).padStart(2, "0");
    const mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${mon}-${year}`;
  };

  const formatMaybeTimeOnly = (raw: any) => {
    if (!raw) return "-";
    return String(raw).slice(0, 8);
  };

  const formatMaybeHrs = (raw: any) => {
    const n = Number(raw);
    return Number.isNaN(n) ? renderDisplay(raw) : n.toFixed(2);
  };

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #d1cfcf",
        borderRadius: "10px",
        margin: "30px 32px",
      }}
    >
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalParents} Users
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={
                tempSelectedColumns.length
                  ? tempSelectedColumns.join(", ")
                  : "Select Columns"
              }
              placement="top"
              arrow
              PopperProps={{ modifiers: [{ name: "offset", options: { offset: [0, 10] } }] }}
            >
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next);
                    setVisibleParentColumns(next);
                    setColSearch("");
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr =
                      typeof val === "string" ? (val as string).split(",") : (val as ColumnLabel[]);
                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                      toggleSelectAllOnFiltered();
                      return;
                    }
                    const next = withDefaultsIfEmpty(
                      Array.isArray(arr) ? (arr as ColumnLabel[]) : []
                    );
                    setTempSelectedColumns(next);
                  }}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  IconComponent={() => (
                    <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: "250px",
                        overflowY: "auto",
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" },
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, lineHeight: 1, px: 1 }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              position: "relative",
              left: "-7px",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderTop: "1px solid #d3d3d3", boxShadow: "none", maxWidth: "100%", overflowX: "auto" }}
      >
        <Table sx={{ minWidth: (visibleParentColumns.length + 1) * 170 }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              <TableCell sx={{ width: 56, minWidth: 56, maxWidth: 56 }} />
              {visibleParentColumns.map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    minWidth: 170,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 320,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === label}
                    direction={orderBy === label ? order : "asc"}
                    onClick={() => handleSort(label as ParentOrderCol)}
                    IconComponent={() => <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedParents.length > 0 ? (
              pagedParents.map(({ parent, children }) => {
                const userId = parent?.["User ID"];
                const open = !!expanded[userId];

                return (
                  <React.Fragment key={userId}>
                    <TableRow hover>
                      <TableCell sx={{ width: 56 }}>
                        <IconButton size="small" onClick={() => toggleExpand(userId)} aria-label={open ? "Collapse" : "Expand"}>
                          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </TableCell>

                      {visibleParentColumns.map((label) => {
                        const key = labelToKey[label] as ColumnKey;
                        const raw = parent?.[key];
                        let display: React.ReactNode = "-";
                        if (raw !== undefined && raw !== null && raw !== "") {
                          display = String(raw);
                        }
                        return (
                          <TableCell
                            key={label}
                            sx={{
                              maxWidth: 300,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={typeof display === "string" ? (display as string) : undefined}
                          >
                            {display}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleParentColumns.length + 1}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2, border: "1px solid #eee", borderRadius: 1 }}>
                            <Typography sx={{ fontWeight: 600, px: 2, pt: 2 }}>
                              Task Details for User: {renderDisplay(userId)}
                            </Typography>

                            <Table size="small" sx={{ mt: 1 }}>
                              <TableHead>
                                <TableRow>
                                  {DETAIL_COLUMNS.map((h) => (
                                    <TableCell key={`d-${h}`}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {children && children.length ? (
                                  children.map((child, idx) => (
                                    <TableRow key={child?.["Task ID"] ?? idx}>
                                      {DETAIL_COLUMNS.map((h) => {
                                        const key = labelToKey[h] as ColumnKey;
                                        const raw = child?.[key];

                                        const isDate =
                                          h === "Transaction Date" ||
                                          h === "Task Start Date" ||
                                          h === "Task End Date" ||
                                          h === "Approval Or Rejection Date" ||
                                          h === "Task Completed Date";

                                        const display = isDate
                                          ? formatDMonY(raw)
                                          : h === "Task Completed Time"
                                          ? formatMaybeTimeOnly(raw)
                                          : h === "Task Completion In (hrs)"
                                          ? formatMaybeHrs(raw)
                                          : renderDisplay(raw);

                                        return (
                                          <TableCell
                                            key={`c-${h}`}
                                            sx={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                            title={typeof display === "string" ? display : undefined}
                                          >
                                            {display}
                                          </TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={DETAIL_COLUMNS.length} align="center">
                                      No tasks for this user
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={visibleParentColumns.length + 1} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
    </Box>
  );
};

export default TaskTrans;
